import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../config';
import PDLHealthRecord from './hr_info';
import InitialHealthAssessment from './hr_medical_history';
import PsychiatricHistory from './hr_psychiatric_history';
import DrugsHistory from './hr_drugs_history';
import PhysicalExamination from './hr_physical_examination';
import TBScreening from './hr_tb_screening';
import TBConclusion from './hr_tb_conclusion';
import bodyMarkingService from '../services/bodyMarkingService';
import NotificationBell from './notification';
import ChangePasswordModal from './change_pass';
import styles from '../css-isolated/hr_wizard.module.css';
import authFetch from '../utils/authFetch';

const formSteps = [
  { key: 'basic', label: 'PDL Information', component: PDLHealthRecord },
  { key: 'assessment', label: 'Medical History', component: InitialHealthAssessment },
  { key: 'psychiatric', label: 'Psychiatric History', component: PsychiatricHistory },
  { key: 'drugs', label: 'Substance Use History', component: DrugsHistory },
  { key: 'physical', label: 'Physical Exam', component: PhysicalExamination },
  { key: 'tb', label: 'TB Screening', component: TBScreening },
  { key: 'tb_conclusion', label: 'TB Conclusion', component: TBConclusion },
];

function HRWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get edit mode data from navigation state
  const { mode, initialData } = location.state || {};
  const isEditMode = mode === 'edit';
  const isCopyMode = mode === 'copy';
  
  // System header time state
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userData, setUserData] = useState(null);

  // Update time every second for system header
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load user data from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUserData(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const toggleDarkMode = () => {
    setPendingTheme(!isDarkMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    setShowThemeModal(false);
    
    try {
      setIsDarkMode(pendingTheme);
      localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');
      
      if (userData && userData.id) {
        const response = await authFetch(`${API_BASE_URL}/api/update-theme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userData.id,
            is_darkmode: pendingTheme ? 1 : 0
          }),
        });
        
        if (!response.ok) {
          console.error('Failed to update theme in database');
        }
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const confirmLogout = () => {
    localStorage.removeItem('user');
    setShowLogoutModal(false);
    navigate('/login');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(`.${styles.profileSection}`)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Load step from localStorage or default to 0
  const [step, setStep] = useState(() => {
    if (isEditMode || isCopyMode) return 0; // Always start from step 0 in edit or copy mode
    const savedStep = localStorage.getItem('hr-current-step');
    return savedStep ? parseInt(savedStep, 10) : 0;
  });

  // Track active section within hr_info (PDL Information has 2 internal sections)
  // Load from localStorage to persist section on refresh
  const [activeSection, setActiveSection] = useState(() => {
    if (isEditMode || isCopyMode) return 1; // Always start from section 1 in edit or copy mode
    const savedSection = localStorage.getItem('hr-active-section');
    return savedSection ? parseInt(savedSection, 10) : 1;
  });

  // Load form state from localStorage or default empty state
  const [formState, setFormState] = useState(() => {
    if (isEditMode || isCopyMode) {
      // In edit or copy mode, don't load from localStorage initially
      return {};
    }
    
    // Load from localStorage for new forms
    const savedFormState = localStorage.getItem('hr-form-state');
    if (savedFormState) {
      try {
        return JSON.parse(savedFormState);
      } catch (error) {
        console.error('Error parsing saved HR form state:', error);
      }
    }
    
    return {};
  });

  // Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successTitle, setSuccessTitle] = useState('');

  // Clear form confirmation modal state
  const [clearFormModal, setClearFormModal] = useState({
    show: false
  });

  // Centralized Date Picker Modal State
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [modalMonth, setModalMonth] = useState('');
  const [modalDay, setModalDay] = useState('');
  const [modalYear, setModalYear] = useState('');
  const [modalTextDate, setModalTextDate] = useState('');
  const [modalError, setModalError] = useState('');
  const [datePickerCallback, setDatePickerCallback] = useState(null);
  const [maxDate, setMaxDate] = useState(null); // null means no restriction, or Date object for max date

  // Check if we have saved data on component mount (only for new forms)
  useEffect(() => {
    if (!isEditMode && !isCopyMode) {
      const savedFormState = localStorage.getItem('hr-form-state');
      if (savedFormState) {
        try {
          const parsedData = JSON.parse(savedFormState);
          
          // Log to debug
          // Default values to ignore when checking for meaningful data
          const defaultValues = {
            'name_of_jail': 'BJMP CAR Regional Office (Baguio City)',
            'origin_lock_up': 'BJMP CAR Regional Office (Baguio City)',
            'nameOfJail': 'Santa Cruz District Jail',
            'originLockup': 'Santa Cruz MPS'
          };
          
          // Fields to ignore (auto-populated by AI or system)
          const fieldsToIgnore = ['recommendation'];
          
          // Helper function to check if a value has meaningful data
          const hasMeaningfulData = (value, key = '') => {
            // Skip fields in ignore list
            if (fieldsToIgnore.includes(key)) {
              return false;
            }
            
            // Skip default values
            if (defaultValues[key] && value === defaultValues[key]) {
              return false;
            }
            
            // Check arrays - must have at least one non-empty element
            if (Array.isArray(value)) {
              return value.some(item => {
                if (typeof item === 'string') return item.trim() !== '';
                if (typeof item === 'number') return item !== 0;
                if (typeof item === 'boolean') return false; // Ignore boolean values in arrays
                if (typeof item === 'object' && item !== null) {
                  return Object.entries(item).some(([k, v]) => hasMeaningfulData(v, k));
                }
                return false;
              });
            }
            
            // Check objects (recursively)
            if (typeof value === 'object' && value !== null) {
              return Object.entries(value).some(([k, v]) => hasMeaningfulData(v, k));
            }
            
            // Check primitive values
            if (typeof value === 'string') return value.trim() !== '';
            if (typeof value === 'number') return value !== 0;
            if (typeof value === 'boolean') return false; // Checkboxes/radios default to false, not meaningful
            
            return false;
          };
          
          // Check if there's any meaningful data in any section
          const hasData = Object.entries(parsedData).some(([sectionKey, section]) => {
            if (typeof section === 'object' && section !== null) {
              const result = hasMeaningfulData(section);
              if (result) {
              }
              return result;
            }
            return false;
          });
          if (hasData) {
            // Show notification that data was restored
            setTimeout(() => {
              setSuccessTitle('Form Data Restored');
              setSuccessMessage('Your previously entered data has been restored from your last session.');
              setShowSuccessModal(true);
            }, 500);
          }
        } catch (error) {
          console.error('Error checking saved HR form state:', error);
        }
      }
    }
  }, [isEditMode, isCopyMode]);

  // Initialize form state with data if in edit or copy mode
  useEffect(() => {
    if ((isEditMode || isCopyMode) && initialData) {
      // Convert backend data to frontend format
      const convertedData = convertBackendToFrontend(initialData, isCopyMode);
      
      // In copy mode, keep the photo URL from the fetched data
      // (it will be copied as part of the new record)
      
      setFormState(convertedData);
      
      // Load body markings if we have an hr_info_id (only in copy mode, will create new ones)
      if (isCopyMode && initialData.id) {
        loadBodyMarkings(initialData.id);
      } else if (isEditMode && initialData.id) {
        loadBodyMarkings(initialData.id);
      }
      
      // Clear localStorage for edit/copy mode to prevent conflicts
      localStorage.removeItem('hr-form-state');
      localStorage.removeItem('hr-current-step');
    }
  }, [isEditMode, isCopyMode, initialData]);

  // Function to load body markings
  const loadBodyMarkings = async (hrInfoId) => {
    try {
      const bodyMarks = await bodyMarkingService.getByHrInfoId(hrInfoId);
      
      // Convert backend format to frontend format
      const formattedMarks = {
        front: bodyMarks.front.map(mark => ({
          id: mark.id,
          type: mark.mark_type,
          x: parseFloat(mark.position_x),
          y: parseFloat(mark.position_y),
          description: mark.description || ''
        })),
        back: bodyMarks.back.map(mark => ({
          id: mark.id,
          type: mark.mark_type,
          x: parseFloat(mark.position_x),
          y: parseFloat(mark.position_y),
          description: mark.description || ''
        }))
      };

      // Update form state with body markings
      setFormState(prevState => ({
        ...prevState,
        tb_conclusion: {
          ...prevState.tb_conclusion,
          bodyMarks: formattedMarks
        }
      }));
    } catch (error) {
      console.error('Error loading body markings:', error);
      // Don't fail the entire load if body markings fail
    }
  };

  // Function to convert backend data to frontend format
  const convertBackendToFrontend = (data, isCopyMode = false) => {
    if (!data) return {};

    return {
      basic: {
        // Personal Information
        surname: data.last_name || '',
        firstName: data.first_name || '',
        middleName: data.middle_name || '',
        aka: data.alias_nickname || '',
        photoUrl: data.photo_of_pdl ? `${API_BASE_URL}/api/storage/${data.photo_of_pdl}` : '',
        sex: data.sex || '',
        civilStatus: data.civil_status || '',
        educationalAttainment: data.educ_attainment || '',
        dateOfBirth: data.date_of_birth || '',
        
        // Place of Birth - use individual fields if available, otherwise parse combined field
        municipality: data.pob_municipality || (data.place_of_birth ? data.place_of_birth.split(', ')[0] || '' : ''),
        province: data.pob_province || (data.place_of_birth ? data.place_of_birth.split(', ')[1] || '' : ''),
        region: data.pob_region || (data.place_of_birth ? data.place_of_birth.split(', ')[2] || '' : ''),
        country: data.pob_country || (data.place_of_birth ? data.place_of_birth.split(', ')[3] || '' : ''),
        
        // Commitment Information - clear date in copy mode (new commitment)
        dateOfCommitment: isCopyMode ? '' : (data.date_of_commitment || ''),
        nameOfJail: data.name_of_jail || '',
        originLockup: data.origin_lock_up || '',
        philHealthNo: data.philhealth_no || '',
        
        // Case and Contact Information - exclude in copy mode
        case: isCopyMode ? '' : (data.case_ || ''),
        caseNo: isCopyMode ? '' : (data.case_no || ''),
        contactPerson: data.name_of_contact_person || '',
        relationship: data.relationship || '',
        contactAddress: data.add_of_contact_person || '',
        contactPhone: data.phone_email || '',
        
        // Final Remarks - exclude in copy mode
        finalRemarks: isCopyMode ? '' : (data.final_remarks || ''),
      },
      // Medical History (hr_a_medical_history table)
      assessment: {
        presentHealthConditions: isCopyMode ? [] : (data.hr_present_health_conditions || []).map(c => c.present_health_condition),
        otherHealthCondition: '',
        pediatricHistory: data.hr_a_medical_history?.pediatric_history || '',
        majorAdultIllnesses: data.hr_a_medical_history?.major_adult_illnesses || '',
        majorSurgeries: data.hr_a_medical_history?.major_surgeries || '',
        seriousInjuries: data.hr_a_medical_history?.serious_physical_injuries || '',
        limitations: data.hr_a_medical_history?.limit_in_range_of_motion || '',
        medicationsHistory: data.hr_a_medical_history?.medications_history || '',
        transfusionHistory: data.hr_a_medical_history?.history_of_transfusions || '',
        mentalEmotionalProblems: data.hr_a_medical_history?.mental_emotional_problems || '',
        bloodType: data.hr_a_medical_history?.blood_type || '',
        allergies: data.hr_a_medical_history?.allergies || '',
        // Family History - convert tinyint(4) values to booleans
        familyHistory: {
          hypertension: Boolean(data.hr_a_medical_history?.fm_hypertension),
          stroke: Boolean(data.hr_a_medical_history?.fm_stroke),
          heartAttack: Boolean(data.hr_a_medical_history?.fm_heartAttack),
          diabetes: Boolean(data.hr_a_medical_history?.fm_diabetes),
          asthma: Boolean(data.hr_a_medical_history?.fm_asthma),
          kidneyDisease: Boolean(data.hr_a_medical_history?.fm_kidneyDisease),
          cancer: Boolean(data.hr_a_medical_history?.fm_cancer)
        },
        otherFamilyHistory: data.hr_a_medical_history?.fm_others || '',
      },
      psychiatric: {
        consultationWithPsychiatrist: data.hr_a_psychiatric_history?.consulted_psychiatrist === 1 ? 'Yes' : data.hr_a_psychiatric_history?.consulted_psychiatrist === 0 ? 'No' : '',
        consultationWhen: data.hr_a_psychiatric_history?.consultation_date || '',
        consultationWhere: data.hr_a_psychiatric_history?.confinement_place || '',
        confinementInPsychiatricFacility: data.hr_a_psychiatric_history?.confined === 1 ? 'Yes' : data.hr_a_psychiatric_history?.confined === 0 ? 'No' : '',
        confinementWhen: data.hr_a_psychiatric_history?.confinement_date || '',
        diagnosis: data.hr_a_psychiatric_history?.diagnosis || '',
        medicationTaken: data.hr_a_psychiatric_history?.medication || '',
        arvTreatment: data.hr_a_psychiatric_history?.arv_treatment === 1 ? 'Yes' : data.hr_a_psychiatric_history?.arv_treatment === 0 ? 'No' : '',
        arvTreatmentFrom: data.hr_a_psychiatric_history?.arv_start || '',
        arvTreatmentPlace: data.hr_a_psychiatric_history?.arv_place || '',
        obGyneApplicable: data.hr_a_psychiatric_history?.obgyne === 1 ? 'Yes' : data.hr_a_psychiatric_history?.obgyne === 0 ? 'No' : '',
        lmp: data.hr_a_psychiatric_history?.lmp || '',
        gravida: data.hr_a_psychiatric_history?.g_gravida || '',
        parity: data.hr_a_psychiatric_history?.p_para || '',
        vaccines: data.hr_a_psychiatric_history?.vaccines || ''
      },
      // Drugs History (hr_a_drugs_history table)
      drugs: {
        alcoholDrinker: data.hr_a_drugs_history?.alcohol_drinker === 1 ? 'Yes' : data.hr_a_drugs_history?.alcohol_drinker === 0 ? 'No' : '',
        alcoholAgeAtOnset: data.hr_a_drugs_history?.alcohol_age_at_onset || '',
        alcoholFrequencyPerWeek: data.hr_a_drugs_history?.use_per_week || '',
        alcoholTypeOfDrinks: data.hr_a_drugs_history?.type_of_drinks || '',
        alcoholVolumeConsumed: data.hr_a_drugs_history?.volume_of_alcohol || '',
        smokingStatus: data.hr_a_drugs_history?.smoking || '',
        smokingAgeAtOnset: data.hr_a_drugs_history?.smoking_age_at_onset || '',
        cigarettesPerDay: data.hr_a_drugs_history?.cigarette_a_day || '',
        useOfIllicitDrugs: data.hr_a_drugs_history?.use_drugs === 1 ? 'Yes' : data.hr_a_drugs_history?.use_drugs === 0 ? 'No' : '',
        drugsAgeAtOnset: data.hr_a_drugs_history?.drugs_age_at_onset || '',
        injectingDrugs: data.hr_a_drugs_history?.injecting_drugs === 1 ? 'Yes' : data.hr_a_drugs_history?.injecting_drugs === 0 ? 'No' : '',
        multipleDrugsAtOnce: data.hr_a_drugs_history?.use_more_than_one_drugs === 1 ? 'Yes' : data.hr_a_drugs_history?.use_more_than_one_drugs === 0 ? 'No' : '',
        primary_use: data.hr_a_drugs_history?.primary_use || '',
        pri_frequency: data.hr_a_drugs_history?.pri_frequency || '',
        pri_last_intake: data.hr_a_drugs_history?.pri_last_intake || '',
        secondary_use: data.hr_a_drugs_history?.secondary_use || '',
        sec_frequency: data.hr_a_drugs_history?.sec_frequency || '',
        sec_last_intake: data.hr_a_drugs_history?.sec_last_intake || '',
        other_use: data.hr_a_drugs_history?.other_use || '',
        oth_frequency: data.hr_a_drugs_history?.oth_frequency || '',
        oth_last_intake: data.hr_a_drugs_history?.oth_last_intake || ''
      },
      physical: isCopyMode ? {
        vitals: {
          temperature: '',
          bp: '',
          hr: '',
          rr: '',
          height: '',
          weight: '',
          bmi: ''
        },
        findings: [
          { label: 'General appearance', note: 'upright with coordinated movement, no signs of distress', value: null },
          { label: 'Head, eyes, ears, nose and throat', note: 'Normocephalic, pink palpebral conjunctiva, white and clear sclera, PERLA, no nasal/aural discharges, no tonsillo-pharyngeal congestion', value: null },
          { label: 'Neck', note: 'no lymph nodes palpable', value: null },
          { label: 'Chest/lungs', note: 'symmetrical chest and lung expansion, clear both sounds', value: null },
          { label: 'Heart', note: 'regular rhythm, no abnormal heart sounds', value: null },
          { label: 'Abdomen', note: 'flat, soft, no masses, normal bowel sounds', value: null },
          { label: 'Genito-urinary', note: 'no lesions, no urethral discharges, no palpable hernia', value: null },
          { label: 'Musculo-skeletal', note: 'no gross deformities, no limitation of movement', value: null },
          { label: 'Extremities', note: 'no swelling/deformity, no weakness, good capillary refill, strong pulses, normal flexes', value: null },
          { label: 'Other significant findings (specify)', note: '', value: null }
        ],
        detailedFindings: ''
      } : {
        vitals: {
          temperature: data.hr_b_physical_examination?.temp || '',
          bp: data.hr_b_physical_examination?.bp || '',
          hr: data.hr_b_physical_examination?.hr || '',
          rr: data.hr_b_physical_examination?.rr || '',
          height: data.hr_b_physical_examination?.height || '',
          weight: data.hr_b_physical_examination?.weight || '',
          bmi: data.hr_b_physical_examination?.bmi || ''
        },
        findings: [
          { label: 'General appearance', note: 'upright with coordinated movement, no signs of distress', value: data.hr_b_physical_examination?.general_appearance === 1 ? true : data.hr_b_physical_examination?.general_appearance === 0 ? false : null },
          { label: 'Head, eyes, ears, nose and throat', note: 'Normocephalic, pink palpebral conjunctiva, white and clear sclera, PERLA, no nasal/aural discharges, no tonsillo-pharyngeal congestion', value: data.hr_b_physical_examination?.head === 1 ? true : data.hr_b_physical_examination?.head === 0 ? false : null },
          { label: 'Neck', note: 'no lymph nodes palpable', value: data.hr_b_physical_examination?.neck === 1 ? true : data.hr_b_physical_examination?.neck === 0 ? false : null },
          { label: 'Chest/lungs', note: 'symmetrical chest and lung expansion, clear both sounds', value: data.hr_b_physical_examination?.chest === 1 ? true : data.hr_b_physical_examination?.chest === 0 ? false : null },
          { label: 'Heart', note: 'regular rhythm, no abnormal heart sounds', value: data.hr_b_physical_examination?.heart === 1 ? true : data.hr_b_physical_examination?.heart === 0 ? false : null },
          { label: 'Abdomen', note: 'flat, soft, no masses, normal bowel sounds', value: data.hr_b_physical_examination?.abdomen === 1 ? true : data.hr_b_physical_examination?.abdomen === 0 ? false : null },
          { label: 'Genito-urinary', note: 'no lesions, no urethral discharges, no palpable hernia', value: data.hr_b_physical_examination?.genito_urinary === 1 ? true : data.hr_b_physical_examination?.genito_urinary === 0 ? false : null },
          { label: 'Musculo-skeletal', note: 'no gross deformities, no limitation of movement', value: data.hr_b_physical_examination?.musculo_skeletal === 1 ? true : data.hr_b_physical_examination?.musculo_skeletal === 0 ? false : null },
          { label: 'Extremities', note: 'no swelling/deformity, no weakness, good capillary refill, strong pulses, normal flexes', value: data.hr_b_physical_examination?.extremities === 1 ? true : data.hr_b_physical_examination?.extremities === 0 ? false : null },
          { label: 'Other significant findings (specify)', note: '', value: data.hr_b_physical_examination?.other_findings || null }
        ],
        detailedFindings: data.hr_b_physical_examination?.detailed_findings || ''
      },
      // TB Screening (hr_c_tb_screening table)
      tb: isCopyMode ? {
        checklist: {
          A: null,
          B: null,
          B_known: null,
          C_blood: null,
          C_weight: null,
          C_fever: null,
          D: null,
          E: null,
          F: null
        },
        tbOutcome: '',
        tbExposure: '',
        remarks: {
          presumptiveTB: false,
          presumptiveDRTB: false,
          notTB: false,
          ongoing: false,
          other: false,
          otherText: ''
        },
        finalResult: {
          newCase: false,
          retreatment: false,
          negative: false,
          bc: false,
          cd: false
        }
      } : {
        checklist: {
          A: data.hr_c_tb_screening?.unexplained_cough === 1 ? true : data.hr_c_tb_screening?.unexplained_cough === 0 ? false : null,
          B: data.hr_c_tb_screening?.bmi_low === 1 ? true : data.hr_c_tb_screening?.bmi_low === 0 ? false : null,
          B_known: data.hr_c_tb_screening?.known_medical_condition === 1 ? true : data.hr_c_tb_screening?.known_medical_condition === 0 ? false : null,
          C_blood: data.hr_c_tb_screening?.blood_streaked_sputum === 1 ? true : data.hr_c_tb_screening?.blood_streaked_sputum === 0 ? false : null,
          C_weight: data.hr_c_tb_screening?.weight_loss === 1 ? true : data.hr_c_tb_screening?.weight_loss === 0 ? false : null,
          C_fever: data.hr_c_tb_screening?.persistent_fever === 1 ? true : data.hr_c_tb_screening?.persistent_fever === 0 ? false : null,
          D: data.hr_c_tb_screening?.xray_suggestion === 1 ? true : data.hr_c_tb_screening?.xray_suggestion === 0 ? false : null,
          E: data.hr_c_tb_screening?.tb_treatment_history === 1 ? true : data.hr_c_tb_screening?.tb_treatment_history === 0 ? false : null,
          F: data.hr_c_tb_screening?.exposure_to_tb === 1 ? true : data.hr_c_tb_screening?.exposure_to_tb === 0 ? false : null
        },
        tbOutcome: data.hr_c_tb_screening?.tb_tratment_specify || '',
        tbExposure: data.hr_c_tb_screening?.exposure_specify || '',
        remarks: {
          presumptiveTB: data.hr_c_tb_screening?.remarks === 'Presumptive TB',
          presumptiveDRTB: data.hr_c_tb_screening?.remarks === 'Presumptive DR-TB',
          notTB: data.hr_c_tb_screening?.remarks === 'Not TB',
          ongoing: data.hr_c_tb_screening?.remarks === 'On-going TB treatment',
          other: data.hr_c_tb_screening?.remarks?.startsWith('Other follow-up:') || false,
          otherText: data.hr_c_tb_screening?.remarks?.startsWith('Other follow-up:') ? 
            data.hr_c_tb_screening.remarks.replace('Other follow-up: ', '') : ''
        },
        finalResult: {
          newCase: data.hr_c_tb_screening?.final_result === 'New Case',
          retreatment: data.hr_c_tb_screening?.final_result === 'Retreatment',
          negative: data.hr_c_tb_screening?.final_result === 'Negative',
          bc: data.hr_c_tb_screening?.final_result === 'BC',
          cd: data.hr_c_tb_screening?.final_result === 'CD'
        }
      },
      tb_conclusion: isCopyMode ? {
        conclusion: false,
        furtherExam: {
          sbirt: false,
          philpen: false,
          neuro: false,
          allegation: false
        },
        findings: {
          noInjury: false,
          noMarkings: false
        },
        impressions: ['', '', ''],
        recommendation: '',
        bodyMarks: { front: [], back: [] }
      } : {
        conclusion: data.hr_c_tb_conclusion?.practically_healthy === 1 ? true : false,
        furtherExam: {
          sbirt: data.hr_c_tb_conclusion?.require_sbirt === 1 ? true : false,
          philpen: data.hr_c_tb_conclusion?.require_philpen === 1 ? true : false,
          neuro: data.hr_c_tb_conclusion?.require_neuro_eval === 1 ? true : false,
          allegation: data.hr_c_tb_conclusion?.require_ill_treatment_doc === 1 ? true : false
        },
        findings: {
          noInjury: data.hr_c_tb_conclusion?.no_physical_injury === 1 ? true : false,
          noMarkings: data.hr_c_tb_conclusion?.no_skin_markings === 1 ? true : false
        },
        impressions: [data.hr_c_tb_conclusion?.other_impressions || '', '', ''],
        recommendation: data.hr_c_tb_conclusion?.recommendation || '',
        bodyMarks: { front: [], back: [] }  // Will be loaded separately
      }
    };
  };

  // Save form state to localStorage whenever it changes (except in edit or copy mode)
  useEffect(() => {
    if (!isEditMode && !isCopyMode) {
      try {
        localStorage.setItem('hr-form-state', JSON.stringify(formState));
      } catch (error) {
        console.error('Error saving form state to localStorage:', error);
        if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
          // Deep copy basic to avoid mutating React state
          const stateWithoutPhoto = { ...formState };
          if (stateWithoutPhoto.basic) {
            stateWithoutPhoto.basic = { ...stateWithoutPhoto.basic };
            delete stateWithoutPhoto.basic.photoUrl;
            delete stateWithoutPhoto.basic.photo_of_pdl;
            delete stateWithoutPhoto.basic.photoFile;
          }
          let retrySaved = false;
          try {
            // Try saving without photo
            localStorage.setItem('hr-form-state', JSON.stringify(stateWithoutPhoto));
            retrySaved = true;
          } catch (retryError) {
            // Total failure — nothing saved
          }
          
          setErrorMessage(retrySaved
            ? 'Storage quota exceeded. Your form data has been saved without the photo. Please upload a smaller image (recommended: under 500KB).'
            : 'Storage quota exceeded and form data could not be saved. Please clear browser storage or reduce image size before continuing.'
          );
          setShowErrorModal(true);
        }
      }
    }
  }, [formState, isEditMode, isCopyMode]);

  // Save current step to localStorage whenever it changes (except in edit or copy mode)
  useEffect(() => {
    if (!isEditMode && !isCopyMode) {
      localStorage.setItem('hr-current-step', step.toString());
    }
    // Note: We don't reset activeSection here to allow proper back button navigation
    // The activeSection is managed by handleBack and handleNext functions
  }, [step, isEditMode, isCopyMode]);

  // Save active section to localStorage whenever it changes (except in edit or copy mode)
  useEffect(() => {
    if (!isEditMode && !isCopyMode) {
      localStorage.setItem('hr-active-section', activeSection.toString());
    }
  }, [activeSection, isEditMode, isCopyMode]);

  // Auto-save notification state
  const [showAutoSaveIndicator, setShowAutoSaveIndicator] = useState(false);

  // Theme State
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme !== 'light';
  });
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [pendingTheme, setPendingTheme] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Show auto-save indicator when form data changes
  useEffect(() => {
    if (!isEditMode && !isCopyMode) {
      setShowAutoSaveIndicator(true);
      const timer = setTimeout(() => {
        setShowAutoSaveIndicator(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [formState, isEditMode, isCopyMode]);

  const CurrentForm = formSteps[step].component;
  const currentKey = formSteps[step].key;

  // Navigation handlers
  const handleNext = () => {
    // If on PDL Information (step 0) and not on last section, go to next section
    if (step === 0 && activeSection < 2) {
      setActiveSection(activeSection + 1);
    } else if (step === 1 && activeSection < 2) {
      // If on Medical History (step 1) and not on last section, go to next section
      setActiveSection(activeSection + 1);
    } else if (step === 2 && activeSection < 2) {
      // If on Psychiatric History (step 2) and not on last section, go to next section
      setActiveSection(activeSection + 1);
    } else if (step === 3 && activeSection < 3) {
      // If on Substance Use History (step 3) and not on last section (has 3 sections), go to next section
      setActiveSection(activeSection + 1);
    } else if (step === 4 && activeSection < 2) {
      // If on Physical Exam (step 4) and not on last section (has 2 sections), go to next section
      setActiveSection(activeSection + 1);
    } else if (step === 5 && activeSection < 2) {
      // If on TB Screening (step 5) and not on last section (has 2 sections), go to next section
      setActiveSection(activeSection + 1);
    } else if (step < formSteps.length - 1) {
      // Move to next step and reset to first section
      setStep(step + 1);
      setActiveSection(1);
    }
  };

  const handleBack = () => {
    // If on multi-section steps and not on first section, go to previous section
    if ((step === 0 || step === 1 || step === 2 || step === 3 || step === 4 || step === 5) && activeSection > 1) {
      setActiveSection(activeSection - 1);
    } else if (step > 0) {
      // Move to previous step and set to last section if it's a multi-section step
      const newStep = step - 1;
      setStep(newStep);
      // Set to last section based on the step
      // Only step 0 (PDL Information) has 2 sections
      if (newStep === 0) {
        setActiveSection(2); // PDL Information has section 1 (Personal Info) and section 2 (Commitment & Contact Info)
      } else {
        setActiveSection(1); // All other steps start at section 1
      }
    }
  };

  // Reference to track previous sex value
  const previousSexRef = useRef();

  // Data change handler
  const handleFormChange = (data) => {
    setFormState(prev => ({ ...prev, [currentKey]: data }));
  };

  // Watch for sex changes and clear OB-GYN data if changed from female to male
  useEffect(() => {
    const currentSex = formState.basic?.sex;
    const previousSex = previousSexRef.current;
    
    // If sex changed from female to male, clear OB-GYN data
    if (previousSex === 'F' && currentSex === 'M') {
      setFormState(prev => ({
        ...prev,
        psychiatric: {
          ...prev.psychiatric,
          obGyneApplicable: '',
          lmp: '',
          lmp_month: '',
          lmp_day: '',
          lmp_year: '',
          gravida: '',
          parity: '',
          vaccines: ''
        }
      }));
    }
    
    // Update the ref for next comparison
    previousSexRef.current = currentSex;
  }, [formState.basic?.sex]);

  // Date Picker Helper Functions
  const months = [
    { value: '', label: 'Month' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const getDaysInMonth = (month, year) => {
    if (!month) return 31;
    const monthNum = parseInt(month);
    if (monthNum === 2) {
      if (!year) return 29;
      const yearNum = parseInt(year);
      if ((yearNum % 4 === 0 && yearNum % 100 !== 0) || (yearNum % 400 === 0)) {
        return 29;
      } else {
        return 28;
      }
    } else if ([4, 6, 9, 11].includes(monthNum)) {
      return 30;
    } else {
      return 31;
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatDateToISO = (month, day, year) => {
    if (!month || !day || !year) return '';
    return `${year}-${month}-${day}`;
  };

  // Open Date Picker - Called by child components
  // maxDateParam: optional Date object or 'today' to restrict future dates
  const openDatePicker = (currentValue, callback, maxDateParam = null) => {
    let parsedMonth = '', parsedDay = '', parsedYear = '';
    
    if (currentValue) {
      const date = new Date(currentValue);
      if (!isNaN(date.getTime())) {
        parsedMonth = String(date.getMonth() + 1).padStart(2, '0');
        parsedDay = String(date.getDate()).padStart(2, '0');
        parsedYear = String(date.getFullYear());
      }
    }
    
    // Set max date restriction
    if (maxDateParam === 'today') {
      setMaxDate(new Date());
    } else if (maxDateParam instanceof Date) {
      setMaxDate(maxDateParam);
    } else {
      setMaxDate(null);
    }
    
    setModalMonth(parsedMonth);
    setModalDay(parsedDay);
    setModalYear(parsedYear);
    setModalTextDate(currentValue ? formatDateForDisplay(currentValue) : '');
    setModalError('');
    setDatePickerCallback(() => callback);
    setIsDateModalOpen(true);
  };

  const closeDateModal = () => {
    setIsDateModalOpen(false);
    setModalError('');
  };

  const handleModalTextDateChange = (e) => {
    let val = e.target.value;
    const prevVal = modalTextDate;
    
    // Allow deleting
    if (val.length < prevVal.length) {
      setModalTextDate(val);
      setModalError('');
      return;
    }
    
    // Remove non-digits
    val = val.replace(/\D/g, '');
    
    // Auto-format with slashes
    if (val.length >= 2) {
      val = val.slice(0, 2) + '/' + val.slice(2);
    }
    if (val.length >= 5) {
      val = val.slice(0, 5) + '/' + val.slice(5);
    }
    if (val.length > 10) {
      val = val.slice(0, 10);
    }
    
    setModalTextDate(val);
    setModalError('');
    
    // Parse and update dropdowns
    const parts = val.split('/');
    if (parts[0] && parts[0].length === 2) {
      const m = parseInt(parts[0]);
      if (m >= 1 && m <= 12) {
        setModalMonth(String(m));
      }
    }
    if (parts[1] && parts[1].length === 2) {
      const d = parseInt(parts[1]);
      const maxDays = getDaysInMonth(parts[0], parts[2]);
      if (d >= 1 && d <= maxDays) {
        setModalDay(String(d));
      }
    }
    if (parts[2] && parts[2].length === 4) {
      const y = parseInt(parts[2]);
      const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear();
      if (y <= maxYear) {
        setModalYear(String(y));
      }
    }
  };

  const handleModalApply = () => {
    if (modalMonth && modalDay && modalYear) {
      const formattedDate = formatDateToISO(modalMonth, modalDay, modalYear);
      
      // Check if date exceeds maxDate restriction
      if (maxDate) {
        const selectedDate = new Date(formattedDate);
        if (selectedDate > maxDate) {
          setModalError('Date cannot be in the future');
          return;
        }
      }
      
      if (datePickerCallback) {
        datePickerCallback(formattedDate);
      }
      closeDateModal();
    } else {
      setModalError('Please select or enter a valid date');
    }
  };

  const handleModalClear = () => {
    setModalMonth('');
    setModalDay('');
    setModalYear('');
    setModalTextDate('');
    setModalError('');
  };

  // Calculate completion percentage for each form section
  const calculateSectionCompletion = (sectionKey, data) => {
    if (!data) return 0;
    
    let totalFields = 0;
    let completedFields = 0;
    
    switch (sectionKey) {
      case 'basic':
        // Required fields for PDL Information (excluding optional fields: middleName, aka, philHealthNo, contactPerson, relationship, contactAddress, contactPhone, photoUrl)
        // Region, Province, Municipality are conditionally required (only if country is Philippines)
        const basicFields = [
          'surname', 'firstName', 'sex', 'civilStatus', 
          'educationalAttainment', 'dateOfBirth', 'country', 
          'dateOfCommitment', 'nameOfJail', 'originLockup', 
          'case', 'caseNo'
        ];
        
        // Add location fields only if country is Philippines
        if (data.country === 'Philippines') {
          basicFields.push('municipality', 'province', 'region');
        }
        
        totalFields = basicFields.length;
        completedFields = basicFields.filter(field => data[field]?.toString().trim()).length;
        break;
        
      case 'assessment':
        // Medical History - focus on key required fields
        const assessmentFields = ['presentHealthConditions'];
        totalFields = assessmentFields.length;
        completedFields = assessmentFields.filter(field => {
          const val = data[field];
          return Array.isArray(val) ? val.length > 0 : val?.toString().trim();
        }).length;
        
        // Add bonus for optional fields filled
        const optionalFields = [
          'bloodType', 'pediatricHistory', 'majorAdultIllnesses', 'majorSurgeries', 'seriousInjuries',
          'limitations', 'medicationsHistory', 'transfusionHistory', 
          'mentalEmotionalProblems', 'allergies', 'otherFamilyHistory'
        ];
        const optionalCompleted = optionalFields.filter(field => data[field]?.toString().trim()).length;
        const familyHistoryCompleted = data.familyHistory ? Object.values(data.familyHistory).filter(Boolean).length : 0;
        
        // Weight: required fields 70%, optional fields 30%
        const requiredPercent = (completedFields / totalFields) * 0.7;
        const optionalPercent = ((optionalCompleted + (familyHistoryCompleted > 0 ? 1 : 0)) / (optionalFields.length + 1)) * 0.3;
        return Math.min(100, (requiredPercent + optionalPercent) * 100);
        
      case 'psychiatric':
        // Only 4 base Yes/No fields are required, all dependent fields are optional
        const psychiatricFields = ['consultationWithPsychiatrist', 'confinementInPsychiatricFacility', 'arvTreatment'];
        
        // Add OB/GYNE applicable only for female PDLs
        const currentSex = formState.basic?.sex;
        if (currentSex === 'F') {
          psychiatricFields.push('obGyneApplicable');
        }
        
        totalFields = psychiatricFields.length;
        completedFields = psychiatricFields.filter(field => data[field]?.toString().trim()).length;
        
        // Add bonus for optional fields filled
        const psychiatricOptionalFields = [
          'consultationWhen', 'consultationWhere', 'confinementWhen', 'diagnosis', 'medicationTaken',
          'arvTreatmentFrom', 'arvTreatmentPlace', 'lmp', 'gravida', 'parity', 'vaccines'
        ];
        const psychiatricOptionalCompleted = psychiatricOptionalFields.filter(field => data[field]?.toString().trim()).length;
        
        // Weight: required fields 70%, optional fields 30%
        const psychiatricRequiredPercent = (completedFields / totalFields) * 0.7;
        const psychiatricOptionalPercent = (psychiatricOptionalCompleted / psychiatricOptionalFields.length) * 0.3;
        return Math.min(100, (psychiatricRequiredPercent + psychiatricOptionalPercent) * 100);
        
      case 'drugs':
        // Count required Yes/No fields and their dependent fields
        let drugsTotal = 3; // Base required fields
        let drugsCompleted = 0;
        
        if (data.alcoholDrinker?.trim()) {
          drugsCompleted++;
          if (data.alcoholDrinker === 'Yes') {
            drugsTotal += 4; // alcoholAgeAtOnset, alcoholFrequencyPerWeek, alcoholVolumeConsumed, alcoholTypeOfDrinks
            if (data.alcoholAgeAtOnset?.trim()) drugsCompleted++;
            if (data.alcoholFrequencyPerWeek?.trim()) drugsCompleted++;
            if (data.alcoholVolumeConsumed?.trim()) drugsCompleted++;
            if (data.alcoholTypeOfDrinks?.trim()) drugsCompleted++;
          }
        }
        
        if (data.smokingStatus?.trim()) {
          drugsCompleted++;
          if (['Current smoker', 'Passive smoker', 'Stopped > a year', 'Stopped < a year'].includes(data.smokingStatus)) {
            drugsTotal += 2; // smokingAgeAtOnset, cigarettesPerDay
            if (data.smokingAgeAtOnset?.trim()) drugsCompleted++;
            if (data.cigarettesPerDay?.trim()) drugsCompleted++;
          }
        }
        
        if (data.useOfIllicitDrugs?.trim()) {
          drugsCompleted++;
          if (data.useOfIllicitDrugs === 'Yes') {
            drugsTotal += 3; // drugsAgeAtOnset, injectingDrugs, multipleDrugsAtOnce
            if (data.drugsAgeAtOnset?.trim()) drugsCompleted++;
            if (data.injectingDrugs?.trim()) drugsCompleted++;
            if (data.multipleDrugsAtOnce?.trim()) {
              drugsCompleted++;
              if (data.multipleDrugsAtOnce === 'Yes') {
                drugsTotal += 2; // primary_use frequency and lastIntake
                if (data.pri_frequency?.trim()) drugsCompleted++;
                if (data.pri_last_intake?.trim()) drugsCompleted++;
              }
            }
          }
        }
        
        totalFields = drugsTotal;
        completedFields = drugsCompleted;
        break;
        
      case 'physical':
        // Vitals (7 fields) + Findings (9 required). "Other significant findings" and "Detailed Findings" are optional
        const vitalsFields = ['temperature', 'bp', 'hr', 'rr', 'height', 'weight', 'bmi'];
        const vitalsCompleted = vitalsFields.filter(field => 
          data.vitals?.[field]?.toString().trim()
        ).length;
        
        // Check first 9 findings (excluding "Other significant findings" at index 9)
        const findingsCompleted = data.findings ? 
          data.findings.slice(0, 9).filter(finding => 
            finding.value !== null && finding.value !== undefined
          ).length : 0;
        
        totalFields = vitalsFields.length + 9; // 7 vitals + 9 findings
        completedFields = vitalsCompleted + findingsCompleted;
        
        // Add bonus for optional fields filled
        const physicalOptionalFields = [];
        if (data.detailedFindings?.trim()) physicalOptionalFields.push(1);
        if (data.findings?.[9]?.value) physicalOptionalFields.push(1);
        
        // Weight: required fields 85%, optional fields 15%
        const physicalRequiredPercent = (completedFields / totalFields) * 0.85;
        const physicalOptionalPercent = (physicalOptionalFields.length / 2) * 0.15;
        return Math.min(100, (physicalRequiredPercent + physicalOptionalPercent) * 100);
        
      case 'tb':
        // Checklist items (9 required)
        const checklistItems = ['A', 'B', 'B_known', 'C_blood', 'C_weight', 'C_fever', 'D', 'E', 'F'];
        const checklistCompleted = checklistItems.filter(item => 
          data.checklist?.[item] !== null && data.checklist?.[item] !== undefined
        ).length;
        
        // TB Outcome and TB Exposure (conditional)
        let conditionalFields = 0;
        let conditionalCompleted = 0;
        
        if (data.checklist?.E === true) {
          conditionalFields++;
          if (data.tbOutcome?.trim()) conditionalCompleted++;
        }
        
        if (data.checklist?.F === true) {
          conditionalFields++;
          if (data.tbExposure?.trim()) conditionalCompleted++;
        }
        
        // Remarks (at least one must be selected)
        const remarksSelected = data.remarks && (
          data.remarks.presumptiveTB || data.remarks.presumptiveDRTB || 
          data.remarks.notTB || data.remarks.ongoing || data.remarks.other
        ) ? 1 : 0;
        
        // Final result (at least one must be selected)
        const finalResultSelected = data.finalResult && (
          data.finalResult.newCase || data.finalResult.retreatment || 
          data.finalResult.negative || data.finalResult.bc || data.finalResult.cd
        ) ? 1 : 0;
        
        totalFields = checklistItems.length + conditionalFields + 2; // checklist + conditional + remarks + final result
        completedFields = checklistCompleted + conditionalCompleted + remarksSelected + finalResultSelected;
        break;
        
      case 'tb_conclusion':
        // Only recommendation is required, but let's also consider other fields
        const conclusionCompleted = data.recommendation?.trim() ? 1 : 0;
        
        // Add bonus for other fields filled
        const otherFieldsCompleted = [
          data.conclusion !== null && data.conclusion !== undefined ? 1 : 0,
          data.furtherExam && Object.values(data.furtherExam).some(Boolean) ? 1 : 0,
          data.findings && Object.values(data.findings).some(Boolean) ? 1 : 0,
          data.impressions && data.impressions.some(imp => imp?.trim()) ? 1 : 0
        ].reduce((sum, val) => sum + val, 0);
        
        totalFields = 1; // Only recommendation required
        completedFields = conclusionCompleted;
        
        // Add bonus percentage for optional fields (up to 50% extra)
        const bonusPercent = (otherFieldsCompleted / 4) * 0.5;
        const basePercent = completedFields / totalFields;
        return Math.min(100, (basePercent + bonusPercent) * 100);
        
      default:
        return 0;
    }
    
    return totalFields > 0 ? (completedFields / totalFields) * 100 : 0;
  };

  // Check if a step's required fields are all filled
  const isStepRequiredFieldsComplete = (stepKey) => {
    const data = formState[stepKey] || {};
    const missing = validateFormSection(stepKey, data);
    return missing.length === 0;
  };

  // Calculate overall progress including partial completion of current step
  const calculateOverallProgress = () => {
    let totalProgress = 0;
    
    // Add actual completion for each visited step
    for (let i = 0; i < step; i++) {
      const stepKey = formSteps[i].key;
      const stepData = formState[stepKey];
      if (stepData) {
        const completion = calculateSectionCompletion(stepKey, stepData);
        totalProgress += Math.min(100, Math.max(0, completion || 0));
      }
    }
    
    // Add partial completion for current step
    if (formState[currentKey]) {
      const currentStepCompletion = calculateSectionCompletion(currentKey, formState[currentKey]);
      // Ensure currentStepCompletion is a valid number between 0 and 100
      const validCompletion = Math.min(100, Math.max(0, currentStepCompletion || 0));
      totalProgress += validCompletion;
    }
    
    // Calculate percentage out of total steps (totalProgress is already in percentage)
    const overallPercentage = totalProgress / formSteps.length;
    
    // Ensure the result is between 0 and 100
    const finalPercentage = Math.min(100, Math.max(0, overallPercentage));
    
    return isNaN(finalPercentage) ? 0 : finalPercentage;
  };

  // Validation functions - ALL fields are required
  const validateFormSection = (sectionKey, data) => {
    const missing = [];
    
    switch (sectionKey) {
      case 'basic':
        // PDL Information - ALL fields required except: middleName, aka, philHealthNo, contactPerson, relationship, contactAddress, contactPhone, photoUrl
        if (!data.surname?.trim()) missing.push({ field: 'Surname' });
        if (!data.firstName?.trim()) missing.push({ field: 'First Name' });
        // middleName is optional
        // aka is optional
        if (!data.sex?.trim()) missing.push({ field: 'Sex' });
        if (!data.civilStatus?.trim()) missing.push({ field: 'Civil Status' });
        if (!data.educationalAttainment?.trim()) missing.push({ field: 'Educational Attainment' });
        if (!data.dateOfBirth) missing.push({ field: 'Date of Birth' });
        
        // Region, Province, and Municipality are only required if country is Philippines
        if (data.country?.trim() === 'Philippines') {
          if (!data.municipality?.trim()) missing.push({ field: 'Municipality/City' });
          if (!data.province?.trim()) missing.push({ field: 'Province' });
          if (!data.region?.trim()) missing.push({ field: 'Region' });
        }
        
        if (!data.country?.trim()) missing.push({ field: 'Country' });
        if (!data.dateOfCommitment) missing.push({ field: 'Date of Commitment' });
        if (!data.nameOfJail?.trim()) missing.push({ field: 'Name of Jail' });
        if (!data.originLockup?.trim()) missing.push({ field: 'Origin Lock-up' });
        // philHealthNo is optional
        if (!data.case?.trim()) missing.push({ field: 'Case' });
        if (!data.caseNo?.trim()) missing.push({ field: 'Case No.' });
        // contactPerson is optional
        // relationship is optional
        // contactAddress is optional
        // contactPhone is optional
        // photoUrl is optional
        break;
        
      case 'assessment':
        // Medical History - At least one Present Health Condition is required
        if (!data.presentHealthConditions || data.presentHealthConditions.length === 0) missing.push({ field: 'Present Health Condition' });
        break;
        
      case 'psychiatric':
        // Psychiatric History - Only 4 base Yes/No fields are required, all dependent fields are optional
        if (!data.consultationWithPsychiatrist?.trim()) missing.push({ field: 'Consultation with Psychiatrist' });
        if (!data.confinementInPsychiatricFacility?.trim()) missing.push({ field: 'Confinement in Psychiatric Facility' });
        if (!data.arvTreatment?.trim()) missing.push({ field: 'ARV Treatment' });
        
        // OB/GYNE applicable is only required for female PDLs
        const pdlSex = formState.basic?.sex;
        if (pdlSex === 'F') {
          if (!data.obGyneApplicable?.trim()) missing.push({ field: 'OB/GYNE Applicable' });
        }
        break;
        
      case 'drugs':
        // Substance Use History - Only Yes/No selections are required, related fields only when applicable
        if (!data.alcoholDrinker?.trim()) missing.push({ field: 'Alcohol Drinker' });
        if (data.alcoholDrinker === 'Yes') {
          if (!data.alcoholAgeAtOnset?.trim()) missing.push({ field: 'Alcohol Age at Onset' });
          if (!data.alcoholFrequencyPerWeek?.trim()) missing.push({ field: 'Alcohol Frequency per Week' });
          if (!data.alcoholVolumeConsumed?.trim()) missing.push({ field: 'Alcohol Volume Consumed' });
          if (!data.alcoholTypeOfDrinks?.trim()) missing.push({ field: 'Alcohol Type of Drinks' });
        }
        
        if (!data.smokingStatus?.trim()) missing.push({ field: 'Smoking Status' });
        if (['Current smoker', 'Passive smoker', 'Stopped > a year', 'Stopped < a year'].includes(data.smokingStatus)) {
          if (!data.smokingAgeAtOnset?.trim()) missing.push({ field: 'Smoking Age at Onset' });
          if (!data.cigarettesPerDay?.trim()) missing.push({ field: 'Cigarettes per Day' });
        }
        
        if (!data.useOfIllicitDrugs?.trim()) missing.push({ field: 'Use of Illicit Drugs' });
        if (data.useOfIllicitDrugs === 'Yes') {
          if (!data.drugsAgeAtOnset?.trim()) missing.push({ field: 'Drugs Age at Onset' });
          if (!data.injectingDrugs?.trim()) missing.push({ field: 'Injecting Drugs' });
          if (!data.multipleDrugsAtOnce?.trim()) missing.push({ field: 'Multiple Drugs at Once' });
          
          // Only require drug use details if user uses multiple drugs at once
          if (data.multipleDrugsAtOnce === 'Yes') {
            if (!data.pri_frequency?.trim()) missing.push({ field: 'Primary Use Frequency' });
            if (!data.pri_last_intake?.trim()) missing.push({ field: 'Primary Use Last Intake' });
          }
        }
        break;
        
      case 'physical':
        // Physical Examination - Vitals and first 9 findings required, "Other significant findings" and "Detailed Findings" are optional
        if (!data.vitals?.temperature) missing.push({ field: 'Temperature' });
        if (!data.vitals?.bp?.trim()) missing.push({ field: 'Blood Pressure' });
        if (!data.vitals?.hr) missing.push({ field: 'Heart Rate' });
        if (!data.vitals?.rr) missing.push({ field: 'Respiratory Rate' });
        if (!data.vitals?.height) missing.push({ field: 'Height' });
        if (!data.vitals?.weight) missing.push({ field: 'Weight' });
        if (!data.vitals?.bmi) missing.push({ field: 'BMI' });
        // Check first 9 findings (excluding "Other significant findings" at index 9)
        if (!data.findings || data.findings.length < 10) {
          missing.push({ field: 'Physical Examination Findings (first 9 items)' });
        } else {
          data.findings.forEach((finding, index) => {
            // Skip index 9 (Other significant findings) - it's optional
            if (index !== 9 && (finding.value === null || finding.value === undefined)) {
              missing.push({ field: `${finding.label} Finding` });
            }
          });
        }
        // Detailed Findings is now optional
        break;
        
      case 'tb':
        // TB Screening - ALL fields required (accept both true and false as valid boolean answers)
        if (data.checklist?.A === null || data.checklist?.A === undefined) {
          missing.push({ field: 'TB Checklist A' });
        }
        if (data.checklist?.B === null || data.checklist?.B === undefined) {
          missing.push({ field: 'TB Checklist B' });
        }
        if (data.checklist?.B_known === null || data.checklist?.B_known === undefined) {
          missing.push({ field: 'Known medical conditions' });
        }
        if (data.checklist?.C_blood === null || data.checklist?.C_blood === undefined) {
          missing.push({ field: 'TB Checklist C Blood' });
        }
        if (data.checklist?.C_weight === null || data.checklist?.C_weight === undefined) {
          missing.push({ field: 'TB Checklist C Weight' });
        }
        if (data.checklist?.C_fever === null || data.checklist?.C_fever === undefined) {
          missing.push({ field: 'TB Checklist C Fever' });
        }
        if (data.checklist?.D === null || data.checklist?.D === undefined) {
          missing.push({ field: 'TB Checklist D' });
        }
        if (data.checklist?.E === null || data.checklist?.E === undefined) {
          missing.push({ field: 'TB Checklist E' });
        }
        if (data.checklist?.F === null || data.checklist?.F === undefined) {
          missing.push({ field: 'TB Checklist F' });
        }
        // Check that TB Outcome and TB Exposure have content when true is selected
        if (data.checklist?.E === true && (!data.tbOutcome || data.tbOutcome.trim() === '')) {
          missing.push({ field: 'TB Outcome (required when History of previous TB treatment is Yes)' });
        }
        if (data.checklist?.F === true && (!data.tbExposure || data.tbExposure.trim() === '')) {
          missing.push({ field: 'TB Exposure (required when Exposure to TB case is Yes)' });
        }
        
        // Check that at least one remark is selected
        const remarksSelected = data.remarks && (
          data.remarks.presumptiveTB || 
          data.remarks.presumptiveDRTB || 
          data.remarks.notTB || 
          data.remarks.ongoing || 
          data.remarks.other
        );
        if (!remarksSelected) {
          missing.push({ field: 'Remarks (at least one option must be selected)' });
        }
        
        // Check that at least one final result is selected
        const finalResultSelected = data.finalResult && (
          data.finalResult.newCase || 
          data.finalResult.retreatment || 
          data.finalResult.negative || 
          data.finalResult.bc || 
          data.finalResult.cd
        );
        if (!finalResultSelected) {
          missing.push({ field: 'Final Result (at least one option must be selected)' });
        }
        break;
        
      case 'tb_conclusion':
        // TB Conclusion - Only Recommendation is required
        if (!data.recommendation?.trim()) missing.push({ field: 'Recommendation' });
        break;
        
      default:
        // No validation for unknown sections
        break;
    }
    
    return missing;
  };

  const validateAllSections = () => {
    // Validate sections one by one, stopping at first section with missing fields
    for (const stepObj of formSteps) {
      const sectionData = formState[stepObj.key] || {};
      const sectionMissing = validateFormSection(stepObj.key, sectionData);
      
      if (sectionMissing.length > 0) {
        // Return only the missing fields from the first incomplete section
        return sectionMissing.map(field => ({
          ...field,
          section: stepObj.label // Use the tab label from formSteps
        }));
      }
    }
    
    return []; // All sections are complete
  };

  const showValidationError = (missing) => {
    setMissingFields(missing);
    setShowValidationModal(true);
  };

  const goToSection = (sectionName) => {
    const sectionIndex = formSteps.findIndex(step => step.label === sectionName);
    if (sectionIndex !== -1) {
      setStep(sectionIndex);
    }
    setShowValidationModal(false);
  };

  const mapHrInfoFields = (data) => {
    // Extract file path from photo URL if it contains the full URL
    const extractPhotoPath = (photoUrl) => {
      if (!photoUrl) return '';
      
      // If it's already a relative path (starts with pdl_photos/), return as is
      if (photoUrl.startsWith('pdl_photos/')) {
        return photoUrl;
      }
      
      // If it's a base64 image, return as is for the controller to handle
      if (photoUrl.startsWith('data:image/')) {
        return photoUrl;
      }
      
      // If it's a full URL, extract just the path after the last 'storage/'
      if (photoUrl.includes('storage/')) {
        // Find the last occurrence of 'storage/' to handle multiple concatenations
        const lastStorageIndex = photoUrl.lastIndexOf('storage/');
        if (lastStorageIndex !== -1) {
          const pathAfterStorage = photoUrl.substring(lastStorageIndex + 8); // 8 = length of 'storage/'
          return pathAfterStorage;
        }
      }
      
      // Otherwise return as is
      return photoUrl;
    };

    return {
      last_name: data.surname,
      first_name: data.firstName,
      middle_name: data.middleName || '',
      alias_nickname: data.aka || '',
      photo_of_pdl: extractPhotoPath(data.photoUrl),
      sex: data.sex,
      civil_status: data.civilStatus,
      educ_attainment: data.educationalAttainment,
      date_of_birth: data.dateOfBirth,
      place_of_birth: [data.municipality, data.province, data.region, data.country].filter(Boolean).join(', '),
      pob_municipality: data.municipality || '',
      pob_province: data.province || '',
      pob_region: data.region || '',
      pob_country: data.country || '',
      date_of_commitment: data.dateOfCommitment,
      name_of_jail: data.nameOfJail,
      origin_lock_up: data.originLockup,
      philhealth_no: data.philHealthNo || '',
      case_: data.case,
      case_no: data.caseNo,
      name_of_contact_person: data.contactPerson || '',
      relationship: data.relationship || '',
      add_of_contact_person: data.contactAddress || '',
      phone_email: data.contactPhone || '',
    };
  };

  // Function to map medical history data from frontend format to backend format
  const mapMedicalHistoryFields = (data) => {
    // Convert boolean values to integers (0/1)
    const booleanToInt = (value) => value ? 1 : 0;
    
    return {
      pediatric_history: data.pediatricHistory || '',
      major_adult_illnesses: data.majorAdultIllnesses || '',
      major_surgeries: data.majorSurgeries || '',
      serious_physical_injuries: data.seriousInjuries || '',
      limit_in_range_of_motion: data.limitations || '',
      medications_history: data.medicationsHistory || '',
      history_of_transfusions: data.transfusionHistory || '',
      mental_emotional_problems: data.mentalEmotionalProblems || '',
      blood_type: data.bloodType || '',
      allergies: data.allergies || '',
      // Convert boolean values to 0/1 for family history
      fm_hypertension: booleanToInt(data.familyHistory?.hypertension),
      fm_stroke: booleanToInt(data.familyHistory?.stroke),
      fm_heartAttack: booleanToInt(data.familyHistory?.heartAttack),
      fm_diabetes: booleanToInt(data.familyHistory?.diabetes),
      fm_asthma: booleanToInt(data.familyHistory?.asthma),
      fm_kidneyDisease: booleanToInt(data.familyHistory?.kidneyDisease),
      fm_cancer: booleanToInt(data.familyHistory?.cancer),
      fm_others: data.otherFamilyHistory || ''
    };
  };

  // Function to map psychiatric history data from frontend format to backend format
  const mapPsychiatricHistoryFields = (data) => {
    // Convert boolean strings to integers (0/1)
    const booleanToInt = (value) => {
      if (value === 'Yes') return 1;
      if (value === 'No') return 0;
      return null;
    };

    // Convert empty strings to null for date fields
    const convertToDate = (value) => {
      if (value === null || value === undefined) return null;
      const stringValue = String(value).trim();
      return stringValue !== '' ? stringValue : null;
    };

    // Convert empty strings to null for text fields
    const convertToString = (value) => {
      if (value === null || value === undefined) return null;
      const stringValue = String(value).trim();
      return stringValue !== '' ? stringValue : null;
    };

    return {
      consulted_psychiatrist: booleanToInt(data.consultationWithPsychiatrist),
      consultation_date: convertToDate(data.consultationWhen),
      confined: booleanToInt(data.confinementInPsychiatricFacility),
      confinement_date: convertToDate(data.confinementWhen),
      confinement_place: convertToString(data.consultationWhere),
      diagnosis: convertToString(data.diagnosis),
      medication: convertToString(data.medicationTaken),
      arv_treatment: booleanToInt(data.arvTreatment),
      arv_start: convertToDate(data.arvTreatmentFrom),
      arv_place: convertToString(data.arvTreatmentPlace),
      obgyne: booleanToInt(data.obGyneApplicable),
      lmp: convertToDate(data.lmp),
      g_gravida: convertToString(data.gravida),
      p_para: convertToString(data.parity),
      vaccines: convertToString(data.vaccines)
    };
  };

  // Function to map drugs history data from frontend format to backend format
  const mapDrugsHistoryFields = (data) => {
    // Convert boolean strings to actual booleans for required fields
    const convertToBool = (value) => {
      if (value === 'Yes') return true;
      if (value === 'No') return false;
      return null;
    };

    // Convert empty strings to null
    const convertToString = (value) => {
      if (value === null || value === undefined) return null;
      const stringValue = String(value).trim();
      return stringValue !== '' ? stringValue : null;
    };

    // Handle smoking status - store the actual status string
    const convertSmokingStatus = (value) => {
      const validStatuses = ['Never smoked', 'Current smoker', 'Passive smoker', 'Stopped > a year', 'Stopped < a year'];
      return validStatuses.includes(value) ? value : null;
    };

    return {
      alcohol_drinker: convertToBool(data.alcoholDrinker),
      alcohol_age_at_onset: convertToString(data.alcoholAgeAtOnset),
      use_per_week: convertToString(data.alcoholFrequencyPerWeek),
      type_of_drinks: convertToString(data.alcoholTypeOfDrinks),
      volume_of_alcohol: convertToString(data.alcoholVolumeConsumed),
      smoking: convertSmokingStatus(data.smokingStatus),
      smoking_age_at_onset: convertToString(data.smokingAgeAtOnset),
      cigarette_a_day: convertToString(data.cigarettesPerDay),
      use_drugs: convertToBool(data.useOfIllicitDrugs),
      drugs_age_at_onset: convertToString(data.drugsAgeAtOnset),
      injecting_drugs: convertToBool(data.injectingDrugs),
      use_more_than_one_drugs: convertToBool(data.multipleDrugsAtOnce),
      primary_use: convertToString(data.primary_use),
      pri_frequency: convertToString(data.pri_frequency),
      pri_last_intake: convertToString(data.pri_last_intake),
      secondary_use: convertToString(data.secondary_use),
      sec_frequency: convertToString(data.sec_frequency),
      sec_last_intake: convertToString(data.sec_last_intake),
      other_use: convertToString(data.other_use),
      oth_frequency: convertToString(data.oth_frequency),
      oth_last_intake: convertToString(data.oth_last_intake),
    };
  };

  // Function to map TB screening data from frontend format to backend format
  const mapTBScreeningFields = (data) => {
    // Convert boolean strings to actual booleans or integers based on database requirements
    const convertToBoolInt = (value) => {
      if (value === true) return 1;
      if (value === false) return 0;
      return null;
    };

    // Convert empty strings to null
    const convertToString = (value) => {
      if (value === null || value === undefined) return null;
      const stringValue = String(value).trim();
      return stringValue !== '' ? stringValue : null;
    };

    // Process remarks - only one should be true
    let remarks = null;
    if (data.remarks?.presumptiveTB) remarks = 'Presumptive TB';
    else if (data.remarks?.presumptiveDRTB) remarks = 'Presumptive DR-TB';
    else if (data.remarks?.notTB) remarks = 'Not TB';
    else if (data.remarks?.ongoing) remarks = 'On-going TB treatment';
    else if (data.remarks?.other && data.remarks?.otherText) remarks = `Other follow-up: ${data.remarks.otherText}`;

    // Process final result - only one should be true
    let finalResult = null;
    if (data.finalResult?.newCase) finalResult = 'New Case';
    else if (data.finalResult?.retreatment) finalResult = 'Retreatment';
    else if (data.finalResult?.negative) finalResult = 'Negative';
    else if (data.finalResult?.bc) finalResult = 'BC';
    else if (data.finalResult?.cd) finalResult = 'CD';

    return {
      hr_info_id: data.hr_info_id,
      unexplained_cough: convertToBoolInt(data.checklist?.A),
      bmi_low: convertToBoolInt(data.checklist?.B),
      known_medical_condition: convertToBoolInt(data.checklist?.B_known),
      blood_streaked_sputum: convertToBoolInt(data.checklist?.C_blood),
      weight_loss: convertToBoolInt(data.checklist?.C_weight),
      persistent_fever: convertToBoolInt(data.checklist?.C_fever),
      xray_suggestion: convertToBoolInt(data.checklist?.D),
      tb_treatment_history: convertToBoolInt(data.checklist?.E),
      tb_tratment_specify: convertToString(data.tbOutcome),
      exposure_to_tb: convertToBoolInt(data.checklist?.F),
      exposure_specify: convertToString(data.tbExposure),
      remarks: remarks,
      final_result: finalResult
    };
  };

  // Function to map physical examination data from frontend format to backend format
  const mapPhysicalExaminationFields = (data) => {
    // Convert empty strings to null
    const convertToString = (value) => {
      if (value === null || value === undefined) return null;
      const stringValue = String(value).trim();
      return stringValue !== '' ? stringValue : null;
    };

    // Convert boolean to tinyint (0/1)
    const convertToBoolInt = (value) => {
      if (value === true) return 1;
      if (value === false) return 0;
      return null;
    };

    // Extract vitals data
    const vitals = data.vitals || {};
    
    // Extract findings data - map array to individual fields
    const findings = data.findings || [];
    
    return {
      temp: convertToString(vitals.temperature),
      bp: convertToString(vitals.bp),
      hr: convertToString(vitals.hr),
      rr: convertToString(vitals.rr),
      height: convertToString(vitals.height),
      weight: convertToString(vitals.weight),
      bmi: convertToString(vitals.bmi),
      general_appearance: findings[0] ? convertToBoolInt(findings[0].value) : null,
      head: findings[1] ? convertToBoolInt(findings[1].value) : null,
      neck: findings[2] ? convertToBoolInt(findings[2].value) : null,
      chest: findings[3] ? convertToBoolInt(findings[3].value) : null,
      heart: findings[4] ? convertToBoolInt(findings[4].value) : null,
      abdomen: findings[5] ? convertToBoolInt(findings[5].value) : null,
      genito_urinary: findings[6] ? convertToBoolInt(findings[6].value) : null,
      musculo_skeletal: findings[7] ? convertToBoolInt(findings[7].value) : null,
      extremities: findings[8] ? convertToBoolInt(findings[8].value) : null,
      other_findings: findings[9] ? convertToString(findings[9].value) : null,
      detailed_findings: convertToString(data.detailedFindings)
    };
  };

  // Function to map TB conclusion data from frontend format to backend format
  const mapTBConclusionFields = (data) => {
    // Convert empty strings to null
    const convertToString = (value) => {
      if (value === null || value === undefined) return null;
      const stringValue = String(value).trim();
      return stringValue !== '' ? stringValue : null;
    };

    // Convert boolean to tinyint (0/1)
    const convertToBoolInt = (value) => {
      if (value === true) return 1;
      if (value === false) return 0;
      return null;
    };

    // Extract nested objects
    const findings = data.findings || {};
    const furtherExam = data.furtherExam || {};
    const impressions = data.impressions || [];

    return {
      no_physical_injury: convertToBoolInt(findings.noInjury),
      no_skin_markings: convertToBoolInt(findings.noMarkings),
      practically_healthy: convertToBoolInt(data.conclusion),
      require_sbirt: convertToBoolInt(furtherExam.sbirt),
      require_philpen: convertToBoolInt(furtherExam.philpen),
      require_neuro_eval: convertToBoolInt(furtherExam.neuro),
      require_ill_treatment_doc: convertToBoolInt(furtherExam.allegation),
      other_impressions: convertToString(impressions[0] || ''),
      recommendation: convertToString(data.recommendation)
    };
  };

  const handleFinalSubmit = async () => {
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    
    // Validate all sections before submitting
    const missingFields = validateAllSections();
    
    if (missingFields.length > 0) {
      showValidationError(missingFields);
      setIsSubmitting(false);
      return;
    }

    try {
      let hr_info_id;
      
      // Get the current logged-in nurse's ID
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const nurseId = currentUser?.id || null;
      
      if (isEditMode) {
        // Update existing HR info record
        const hrInfoData = mapHrInfoFields(formState.basic);
        hrInfoData.nurse_updated = nurseId; // Track who updated the record
        
        // If recommendation changed, reset cell_id to null
        const originalRecommendation = initialData.hr_c_tb_conclusion?.recommendation || '';
        const newRecommendation = formState.tb_conclusion?.recommendation?.trim() || '';
        if (newRecommendation !== originalRecommendation) {
          hrInfoData.cell_id = null;
        }
        
        const hrInfoRes = await authFetch(`${API_BASE_URL}/api/hr-info/${initialData.id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          mode: 'cors',
          body: JSON.stringify(hrInfoData)
        });
        
        if (!hrInfoRes.ok) {
          const errorText = await hrInfoRes.text();
          console.error('Failed to update PDL Information:', errorText);
          throw new Error('Failed to update PDL Information: ' + errorText);
        }
        
        hr_info_id = initialData.id; // Use the existing ID
      } else {
        // Create new HR info record (for both new and copy mode)
        const hrInfoData = mapHrInfoFields(formState.basic);
        hrInfoData.nurse_created = nurseId; // Track who created the record
        
        const hrInfoRes = await authFetch(`${API_BASE_URL}/api/hr-info`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          mode: 'cors',
          body: JSON.stringify(hrInfoData)
        });
        
        if (!hrInfoRes.ok) {
          const errorText = await hrInfoRes.text();
          console.error('Failed to create PDL Information:', errorText);
          throw new Error('Failed to create PDL Information: ' + errorText);
        }
        
        const jsonResponse = await hrInfoRes.json();
        hr_info_id = jsonResponse.id;
      }

      // Helper function for making API calls (both create and update)
      const submitData = async (endpoint, data, recordId = null) => {
        const url = recordId 
          ? `${API_BASE_URL}/api/${endpoint}/${recordId}`
          : `${API_BASE_URL}/api/${endpoint}`;
        
        const method = recordId ? 'PUT' : 'POST';
        
        const response = await authFetch(url, {
          method: method,
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          mode: 'cors',
          body: JSON.stringify({ ...data, hr_info_id })
        });
        
        if (!response.ok) {
          const text = await response.text();
          console.error(`Error ${method === 'PUT' ? 'updating' : 'creating'} ${endpoint}:`, text);
          throw new Error(`Failed to ${method === 'PUT' ? 'update' : 'create'} ${endpoint}: ${response.status} ${response.statusText}`);
        }
        return response.json();
      };

      // 2. Submit/Update Medical History
      if (formState.assessment) {
        const mappedMedicalData = mapMedicalHistoryFields(formState.assessment);
        
        const medicalHistoryId = isEditMode ? initialData.hr_a_medical_history?.id : null;
        await submitData('hr-a-medical-history', mappedMedicalData, medicalHistoryId);

        // 2b. Submit present health conditions to separate table
        const conditions = formState.assessment.presentHealthConditions || [];
        if (conditions.length > 0) {
          await authFetch(`${API_BASE_URL}/api/hr-present-health-condition/bulk`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            mode: 'cors',
            body: JSON.stringify({ hr_info_id: hr_info_id, conditions })
          });
        } else if (isEditMode) {
          // If editing and no conditions selected, clear them
          await authFetch(`${API_BASE_URL}/api/hr-present-health-condition/hr-info/${hr_info_id}`, {
            method: 'DELETE',
            headers: { 
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            mode: 'cors'
          });
        }
      }

      // 3. Submit/Update Psychiatric History
      if (formState.psychiatric) {
        const mappedPsychiatricData = mapPsychiatricHistoryFields(formState.psychiatric);
        
        const psychiatricHistoryId = isEditMode ? initialData.hr_a_psychiatric_history?.id : null;
        await submitData('hr-a-psychiatric-history', mappedPsychiatricData, psychiatricHistoryId);
      }

      // 4. Submit/Update Drugs History
      if (formState.drugs) {
        const mappedDrugsData = mapDrugsHistoryFields(formState.drugs);
        
        const drugsHistoryId = isEditMode ? initialData.hr_a_drugs_history?.id : null;
        await submitData('hr-a-drugs-history', mappedDrugsData, drugsHistoryId);
      }

      // 5. Submit/Update Physical Exam
      if (formState.physical) {
        const mappedPhysicalData = mapPhysicalExaminationFields(formState.physical);
        
        const physicalExamId = isEditMode ? initialData.hr_b_physical_examination?.id : null;
        await submitData('hr-b-physical-examination', mappedPhysicalData, physicalExamId);
      }

      // 6. Submit/Update TB Screening
      if (formState.tb) {
        const mappedTBData = mapTBScreeningFields(formState.tb);
        
        const tbScreeningId = isEditMode ? initialData.hr_c_tb_screening?.id : null;
        await submitData('hr-c-tb-screening', mappedTBData, tbScreeningId);
      }

      // 7. Submit/Update TB Conclusion
      if (formState.tb_conclusion) {
        const mappedTBConclusionData = mapTBConclusionFields(formState.tb_conclusion);
        
        const tbConclusionId = isEditMode ? initialData.hr_c_tb_conclusion?.id : null;
        await submitData('hr-c-tb-conclusion', mappedTBConclusionData, tbConclusionId);

        // 8. Submit/Update Body Markings
        if (formState.tb_conclusion.bodyMarks) {
          try {
            await bodyMarkingService.saveBulk(hr_info_id, formState.tb_conclusion.bodyMarks);
          } catch (error) {
            console.error('Error saving body markings:', error);
            console.error('Error details:', error.message);
            // Don't fail the entire submission if body markings fail
          }
        } else {
        }
      }

      // Clear localStorage data after successful submission (only for new records)
      if (!isEditMode) {
        localStorage.removeItem('hr-form-state');
        localStorage.removeItem('hr-current-step');
      }

      // Show success message
      setSuccessTitle(isEditMode ? 'Record Updated Successfully!' : 'Record Saved Successfully!');
      setSuccessMessage(isEditMode ? 'The Health Record has been updated successfully.' : 'All Health Record data has been submitted successfully!');
      setShowSuccessModal(true);
      
    } catch (err) {
      setSuccessTitle('Error Occurred');
      setSuccessMessage('Error submitting Health Record: ' + err.message);
      setShowSuccessModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear form data function
  const clearFormData = () => {
    setClearFormModal({ show: true });
  };

  // Confirm clear form data
  const confirmClearFormData = () => {
    // Reset form state with proper structure to ensure all fields are cleared
    // but keep default values for Name of Jail and Origin Lock-up
    const emptyState = {
      basic: {
        nameOfJail: 'Santa Cruz District Jail',
        originLockup: 'Santa Cruz MPS'
      },
      assessment: {},
      psychiatric: {},
      drugs: {},
      physical: {},
      tb: {},
      tb_conclusion: {}
    };
    setFormState(emptyState);
    setStep(0);
    setActiveSection(1);
    
    // Clear localStorage
    localStorage.removeItem('hr-form-state');
    localStorage.removeItem('hr-current-step');
    localStorage.removeItem('hr-active-section');
    
    // Close modal
    setClearFormModal({ show: false });
    
    // Show success message
    setSuccessTitle('Form Cleared');
    setSuccessMessage('All form data has been cleared successfully.');
    setShowSuccessModal(true);
  };

  // Cancel clear form data
  const cancelClearFormData = () => {
    setClearFormModal({ show: false });
  };

  // Handle success modal OK button click
  const handleSuccessModalOk = () => {
    setShowSuccessModal(false);
    setActiveSection(1);
    // Only navigate if this was a successful submission (not an error or clear form message)
    if (successTitle.includes('Successfully') && !successTitle.includes('Form Cleared')) {
      navigate(isEditMode ? '/pdl-health-record-search' : '/');
    }
  };

  return (
    <div className={`${styles.hrWizardContainer} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
      {/* System Header - Same as JBR Wizard */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={styles.systemLogoImg} />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.systemInfo}>
            <div className={styles.systemTime}>{currentTime.toLocaleTimeString()}</div>
            <div className={styles.systemUser}>{formatDate(currentTime)}</div>
          </div>
          <NotificationBell />
          <div className={styles.profileSection}>
            <div className={styles.profileIcon} onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className={styles.profileDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={styles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className={styles.dropdownDivider}></div>
                <button 
                  className={styles.dropdownItem}
                  onClick={toggleDarkMode}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isDarkMode ? (
                      <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                  </svg>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={styles.dropdownItem} onClick={() => {
                  setShowChangePasswordModal(true);
                  setIsDropdownOpen(false);
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                <button className={styles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Auto-save indicator */}
      {!isEditMode && !isCopyMode && showAutoSaveIndicator && (
        <div className={styles.autoSaveIndicator}>
          <span className={styles.autoSaveIcon}>💾</span>
          <span className={styles.autoSaveText}>Auto-saved</span>
        </div>
      )}
      
      {/* HR Content Area with Sidebar */}
      <div className={styles.hrContentWrapper}>
        {/* Slim Sidebar Navigation */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarTitle}>Navigation</div>
            <ul className={styles.sidebarMenu}>
              <li className={styles.sidebarItem}>
                <button
                  type="button"
                  className={styles.sidebarLink}
                  onClick={() => navigate('/')}
                  title="Dashboard"
                >
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Dashboard</span>
                </button>
              </li>
            </ul>
          </div>
          
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarTitle}>Health Record</div>
            <ul className={styles.sidebarMenu}>
              {formSteps.map((stepObj, index) => {
                const isCurrentStep = index === step;
                const isCompletedStep = index < step;
                
                // Icon mapping for each step
                const getStepIcon = () => {
                  if (isCompletedStep) {
                    return (
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                    );
                  }
                  
                  switch(stepObj.key) {
                    case 'basic': // PDL Information
                      return (
                        <>
                          <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                        </>
                      );
                    case 'assessment': // Medical History
                      return (
                        <>
                          <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <path d="M12 8V16M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </>
                      );
                    case 'psychiatric': // Psychiatric History
                      return (
                        <>
                          <path d="M12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <circle cx="9" cy="10" r="1" fill="currentColor"/>
                          <circle cx="15" cy="10" r="1" fill="currentColor"/>
                        </>
                      );
                    case 'drugs': // Substance Use History
                      return (
                        <>
                          <path d="M4.5 21L12 3L19.5 21H4.5Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <circle cx="12" cy="17" r="1.5" fill="currentColor"/>
                          <path d="M12 14V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </>
                      );
                    case 'physical': // Physical Exam
                      return (
                        <>
                          <path d="M14.5 3H9.5C7.567 3 6 4.567 6 6.5V17.5C6 19.433 7.567 21 9.5 21H14.5C16.433 21 18 19.433 18 17.5V6.5C18 4.567 16.433 3 14.5 3Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <path d="M9 7H15M9 11H15M9 15H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </>
                      );
                    case 'tb': // TB Screening
                      return (
                        <>
                          <path d="M21 10C21 14.418 16.97 18 12 18C11.0334 18 10.1019 17.8786 9.21863 17.6517L5 19L6.34828 14.7814C6.12142 13.8981 6 12.9666 6 12C6 7.582 10.03 4 14.5 4C18.97 4 21 6.582 21 10Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <path d="M12 10V14M12 7H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </>
                      );
                    case 'tb_conclusion': // TB Conclusion
                      return (
                        <>
                          <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </>
                      );
                    default:
                      return <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none"/>;
                  }
                };
                
                return (
                  <li key={stepObj.key} className={styles.sidebarItem}>
                    <button
                      type="button"
                      className={`${styles.sidebarLink} ${isCurrentStep ? styles.active : ''}`}
                      onClick={() => setStep(index)}
                      title={stepObj.label}
                    >
                      <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        {getStepIcon()}
                      </svg>
                      <span style={{ flex: 1 }}>
                        {stepObj.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className={styles.mainContent}>
          {/* Progress Bar */}
          {(() => {
            const overallProgress = calculateOverallProgress();
            const currentStepCompletion = formState[currentKey] 
              ? calculateSectionCompletion(currentKey, formState[currentKey])
              : 0;
            
            
            return (
              <div className={styles.progressBarContainer}>
                
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill}
                    style={{ width: `${Math.max(0, Math.min(100, overallProgress))}%` }}
                  >
                    {/* Show percentage inside the progress bar if it's wide enough */}
                    {overallProgress > 15 && (
                      <span className={styles.progressText}>
                        {Math.round(overallProgress)}%
                      </span>
                    )}
                  </div>
                  {/* Show current step progress as a subtle indicator */}
                  {formState[currentKey] && (
                    <div 
                      className={styles.currentStepIndicator}
                      style={{ 
                        left: `${(step / formSteps.length) * 100}%`,
                        width: `${(1 / formSteps.length) * 100}%`,
                        opacity: Math.max(0.2, currentStepCompletion / 100)
                      }}
                    ></div>
                  )}
                </div>
            <div className={styles.progressSteps}>
              {formSteps.map((stepObj, index) => {
                const isCurrentStep = index === step;
                const isVisitedStep = index < step;
                const hasRequiredFields = isStepRequiredFieldsComplete(stepObj.key);
                const isCompletedStep = isVisitedStep && hasRequiredFields;
                const isIncompleteStep = isVisitedStep && !hasRequiredFields;
                const currentStepCompletion = isCurrentStep && formState[stepObj.key] 
                  ? calculateSectionCompletion(stepObj.key, formState[stepObj.key])
                  : 0;
                
                return (
                  <div key={stepObj.key} className={styles.progressStep}>
                    <div 
                      className={`${styles.stepCircle} ${
                        isCompletedStep ? styles.stepCompleted : 
                        isIncompleteStep ? styles.stepIncomplete :
                        isCurrentStep ? styles.stepCurrent : styles.stepPending
                      }`}
                    >
                      {isCompletedStep || isIncompleteStep ? '' : index + 1}
                    </div>
                    <span className={`${styles.stepLabel} ${
                      index === step ? styles.stepActive : ''
                    }`}>
                      {stepObj.label}
                      {isCurrentStep && currentStepCompletion > 0 && (
                        <span className={styles.stepProgress}>
                          ({Math.round(currentStepCompletion)}%)
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
            );
          })()}

          <div className={styles.formContainer}>
            <CurrentForm
              values={formState[currentKey] || {}}
              onChange={handleFormChange}
              isDarkMode={isDarkMode}
              sex={formState.basic?.sex}
              formState={formState}
              openDatePicker={openDatePicker}
              activeSection={(step === 0 || step === 1 || step === 2 || step === 3 || step === 4 || step === 5) ? activeSection : undefined}
              setActiveSection={(step === 0 || step === 1 || step === 2 || step === 3 || step === 4 || step === 5) ? setActiveSection : undefined}
            />
            <div className={`${styles.hrwizardNavBtns} ${styles.jbrNavButtons}`}>
              {!isEditMode && !isCopyMode && (
                <button 
                  onClick={clearFormData} 
                  className={styles.clearFormButton}
                  title="Clear all form data"
                >
                  Clear Form
                </button>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                {/* Back Button - Show when not on first step OR when on step 0/1/2/3/4/5 with activeSection > 1 */}
                {(step > 0 || ((step === 0 || step === 1 || step === 2 || step === 3 || step === 4 || step === 5) && activeSection > 1)) && (
                  <button 
                    onClick={handleBack} 
                    className={styles.backButton}
                    title="Go to previous step"
                    style={{ 
                      borderTopRightRadius: 0, 
                      borderBottomRightRadius: 0,
                      marginRight: 0
                    }}
                  >
                    <span style={{ marginRight: 6, fontSize: 16 }}>←</span>
                    Back
                  </button>
                )}
                
                {/* Next/Submit Button */}
                {step < formSteps.length - 1 ? (
                  <button 
                    onClick={handleNext} 
                    className={styles.nextButton}
                    style={{ 
                      borderTopLeftRadius: (step > 0 || ((step === 0 || step === 1 || step === 2 || step === 3 || step === 4 || step === 5) && activeSection > 1)) ? 0 : 14, 
                      borderBottomLeftRadius: (step > 0 || ((step === 0 || step === 1 || step === 2 || step === 3 || step === 4 || step === 5) && activeSection > 1)) ? 0 : 14,
                      marginLeft: 0
                    }}
                  >
                    {step === 0 && activeSection === 2 ? 'Medical Record' : 
                     step === 1 && activeSection === 2 ? 'Psychiatric History' : 
                     step === 2 && activeSection === 2 ? 'Substance Use History' : 
                     step === 3 && activeSection === 3 ? 'Physical Exam' : 
                     step === 4 && activeSection === 2 ? 'TB Screening' : 
                     step === 5 && activeSection === 2 ? 'TB Conclusion' : 'Next'}
                    <span style={{ marginLeft: 6, fontSize: 16 }}>→</span>
                  </button>
                ) : (
                  <button 
                    onClick={handleFinalSubmit} 
                    className={styles.submitButton}
                    disabled={isSubmitting}
                    style={{ 
                      borderTopLeftRadius: step > 0 ? 0 : 14, 
                      borderBottomLeftRadius: step > 0 ? 0 : 14,
                      marginLeft: 0
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : (isEditMode ? 'Update Record' : isCopyMode ? 'Create Copy' : 'Submit All')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinnerLarge}></div>
            <div className={styles.loadingText}>
              <h3>{isEditMode ? 'Updating Health Record...' : isCopyMode ? 'Creating Copy of Health Record...' : 'Submitting Health Record...'}</h3>
              <p>Please wait while we process your data</p>
              <div className={styles.loadingProgress}>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className={styles.validationModalOverlay}>
          <div className={styles.validationModalContent}>
            <div className={styles.validationModalHeader}>
              <div className={styles.validationWarningIcon}>⚠️</div>
              <h3>Required Fields Missing</h3>
            </div>
            <div className={styles.validationModalBody}>
              <div className={styles.validationMessage}>
                Please fill in the following required fields:
              </div>
              <div className={styles.validationSectionInfo}>
                <span className={styles.sectionLabel}>Section:</span>
                <span className={styles.sectionName}>{missingFields[0]?.section}</span>
              </div>
              <div className={styles.missingFieldsContainer}>
                <ul className={styles.missingFieldsList}>
                  {missingFields.map((field, index) => (
                    <li key={index} className={styles.missingFieldItem}>
                      <span className={styles.fieldBullet}>•</span>
                      <span className={styles.fieldName}>{field.field}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={styles.validationModalActions}>
              <button 
                onClick={() => goToSection(missingFields[0]?.section)}
                className={styles.btnGoToSection}
              >
                Go to {missingFields[0]?.section}
              </button>
              <button 
                onClick={() => setShowValidationModal(false)}
                className={styles.btnCloseValidation}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={styles.modalOverlay} onClick={handleSuccessModalOk}>
          <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>{successTitle}</h3>
            <p>{successMessage}</p>
            <button className={styles.successBtn} onClick={handleSuccessModalOk}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Clear Form Confirmation Modal */}
      {clearFormModal.show && (
        <div className={styles.clearFormModalOverlay}>
          <div className={styles.clearFormModalContent}>
            <div className={styles.clearFormModalHeader}>
              <div className={styles.clearFormWarningIcon}>⚠️</div>
              <h3>Clear All Form Data</h3>
            </div>
            <div className={styles.clearFormModalBody}>
              <p className={styles.clearFormMessage}>
                Are you sure you want to clear all form data? This action cannot be undone.
              </p>
              <div className={styles.clearFormWarning}>
                <span className={styles.warningBullet}>•</span>
                <span>All entered information will be permanently lost</span>
              </div>
              <div className={styles.clearFormWarning}>
                <span className={styles.warningBullet}>•</span>
                <span>You will need to start over from the beginning</span>
              </div>
            </div>
            <div className={styles.clearFormModalActions}>
              <button 
                className={styles.btnCancelClear}
                onClick={cancelClearFormData}
              >
                Cancel
              </button>
              <button 
                className={styles.btnConfirmClear}
                onClick={confirmClearFormData}
              >
                Clear Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className={styles.modalOverlay} onClick={() => setShowErrorModal(false)}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Centralized Date Picker Modal */}
      {isDateModalOpen && (
        <div className={styles.datePickerOverlay} onClick={closeDateModal}>
          <div className={styles.datePickerModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.datePickerHeader}>
              <h3>📅 Select Date</h3>
              <button className={styles.datePickerCloseBtn} onClick={closeDateModal}>
                ✕
              </button>
            </div>
            <div className={styles.datePickerContent}>
              <div className={styles.dateSelectionGrid}>
                <div className={styles.dateSelectGroup}>
                  <label className={styles.dateLabel}>Month</label>
                  <select
                    className={styles.dateSelect}
                    value={modalMonth}
                    onChange={(e) => {
                      const value = e.target.value;
                      setModalMonth(value);
                      setModalError('');
                      const maxDays = getDaysInMonth(value, modalYear);
                      if (modalDay && parseInt(modalDay) > maxDays) {
                        setModalDay('');
                      }
                      // Update text date field
                      if (value && modalDay && modalYear) {
                        const m = value.padStart(2, '0');
                        const d = modalDay.padStart(2, '0');
                        setModalTextDate(`${m}/${d}/${modalYear}`);
                      }
                    }}
                  >
                    <option value="">Month</option>
                    {months.slice(1).map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.dateSelectGroup}>
                  <label className={styles.dateLabel}>Day</label>
                  <select
                    className={styles.dateSelect}
                    value={modalDay}
                    onChange={(e) => {
                      const value = e.target.value;
                      setModalDay(value);
                      setModalError('');
                      // Update text date field
                      if (modalMonth && value && modalYear) {
                        const m = modalMonth.padStart(2, '0');
                        const d = value.padStart(2, '0');
                        setModalTextDate(`${m}/${d}/${modalYear}`);
                      }
                    }}
                    disabled={!modalMonth}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: getDaysInMonth(modalMonth, modalYear) }, (_, i) => (
                      <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.dateSelectGroup}>
                  <label className={styles.dateLabel}>Year</label>
                  <select
                    className={styles.dateSelect}
                    value={modalYear}
                    onChange={(e) => {
                      const value = e.target.value;
                      setModalYear(value);
                      setModalError('');
                      const maxDays = getDaysInMonth(modalMonth, value);
                      if (modalDay && parseInt(modalDay) > maxDays) {
                        setModalDay('');
                      }
                      // Update text date field
                      if (modalMonth && modalDay && value) {
                        const m = modalMonth.padStart(2, '0');
                        const d = modalDay.padStart(2, '0');
                        setModalTextDate(`${m}/${d}/${value}`);
                      }
                    }}
                  >
                    <option value="">Year</option>
                    {Array.from({ length: 100 }, (_, i) => {
                      const year = (maxDate ? maxDate.getFullYear() : new Date().getFullYear()) - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
              </div>

              <div className={styles.modalRow}>
                <div className={styles.modalField} style={{ flex: 1 }}>
                  <label>Type Date (mm/dd/yyyy)</label>
                  <input
                    type="text"
                    placeholder="mm/dd/yyyy"
                    value={modalTextDate}
                    onChange={handleModalTextDateChange}
                  />
                  {modalError && (
                    <div className={styles.modalError}>{modalError}</div>
                  )}
                  {!modalError && modalMonth && modalDay && modalYear && (
                    <div className={styles.modalHelper}>
                      📋 Select from dropdowns or type the date directly
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.datePickerActions}>
              <button
                className={styles.datePickerClearBtn}
                onClick={handleModalClear}
              >
                Cancel
              </button>
              <button
                className={styles.datePickerConfirmBtn}
                onClick={handleModalApply}
                disabled={!modalMonth || !modalDay || !modalYear}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Theme Change Confirmation Modal */}
      {showThemeModal && (
        <div className={styles.modalOverlay} onClick={cancelThemeChange}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#f59e0b' }}>
            <div className={styles.themeIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}>
              {pendingTheme ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 style={{ color: '#f59e0b' }}>Change Theme</h3>
            <p>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={styles.cancelBtn} onClick={cancelThemeChange}>Cancel</button>
              <button className={styles.themeBtn} onClick={confirmThemeChange} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userData={userData}
        onSuccess={(message) => {
          setSuccessMessage(message || 'Password changed successfully');
          setShowSuccessModal(true);
        }}
        onError={(message) => {
          setErrorMessage(message);
          setShowErrorModal(true);
        }}
      />

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
              <div className={styles.modalOverlay} onClick={cancelLogout}>
                <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
                  <div className={styles.themeIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className={styles.logoutTitle}>Logout</h3>
                  <p>Are you sure you want to logout?</p>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                    <button className={styles.cancelBtn} onClick={cancelLogout}>Cancel</button>
                    <button className={styles.themeBtn} onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
                  </div>
                </div>
              </div>
            )}
    </div>
  );
}

export default HRWizard;